import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Search, MapPin, Phone, Mail, UserPlus, Filter, Building2, Eye, CheckCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  r1_effectue: { label: 'R1 effectue', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  r2_effectue: { label: 'R2 effectue', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  devis_envoye: { label: 'Devis envoye', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  contrat_envoye: { label: 'Contrat envoye', class: 'bg-orange-100 text-orange-700 border-orange-200' },
  facture_envoyee: { label: 'Facture envoyee', class: 'bg-teal-100 text-teal-700 border-teal-200' },
};

export const LeadsHot = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [academyFilter, setAcademyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newProspect, setNewProspect] = useState({
    name: '',
    academy: '',
    contact_email: '',
    contact_phone: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        fetch(`${API}/prospects`, { credentials: 'include' }),
        fetch(`${API}/academies`, { credentials: 'include' }),
      ];
      
      // Si admin, récupérer aussi la liste des partenaires
      if (isAdmin) {
        requests.push(fetch(`${API}/admin/users`, { credentials: 'include' }));
      }
      
      const responses = await Promise.all(requests);
      
      if (responses[0].ok) {
        const data = await responses[0].json();
        setProspects(data);
      }
      
      if (responses[1].ok) {
        const data = await responses[1].json();
        setAcademies(data);
      }
      
      if (isAdmin && responses[2]?.ok) {
        const users = await responses[2].json();
        const commercials = users.filter(u => u.role === 'commercial');
        setPartners(commercials);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProspectStatus = async (prospectId, newStatus) => {
    try {
      const response = await fetch(`${API}/prospects/${prospectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        setProspects(prev =>
          prev.map(p =>
            p.prospect_id === prospectId ? { ...p, status: newStatus } : p
          )
        );
        toast.success('Statut mis à jour');
      }
    } catch (error) {
      console.error('Failed to update prospect:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const reassignProspect = async (prospectId, newOwnerId) => {
    try {
      const response = await fetch(`${API}/prospects/${prospectId}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ assigned_to: newOwnerId }),
      });
      
      if (response.ok) {
        const newOwner = partners.find(p => p.user_id === newOwnerId);
        setProspects(prev =>
          prev.map(p =>
            p.prospect_id === prospectId 
              ? { ...p, assigned_to: newOwnerId, owner_name: newOwner?.name || '', owner_email: newOwner?.email || '' } 
              : p
          )
        );
        toast.success('Lead reassigne avec succes');
      } else {
        toast.error('Erreur lors de la reassignation');
      }
    } catch (error) {
      console.error('Failed to reassign prospect:', error);
      toast.error('Erreur lors de la reassignation');
    }
  };

  const createProspect = async () => {
    if (!newProspect.name) {
      toast.error('Nom requis');
      return;
    }
    
    try {
      const response = await fetch(`${API}/prospects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newProspect),
      });
      
      if (response.ok) {
        toast.success('Prospect créé');
        setIsDialogOpen(false);
        setNewProspect({ name: '', academy: '', contact_email: '', contact_phone: '', notes: '' });
        fetchData();
      }
    } catch (error) {
      console.error('Failed to create prospect:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const filteredProspects = prospects.filter(prospect => {
    const matchesSearch = prospect.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAcademy = academyFilter === 'all' || prospect.academy === academyFilter;
    const matchesStatus = statusFilter === 'all' || prospect.status === statusFilter;
    // Use assigned_to or created_by for partner filter
    const prospectOwner = prospect.assigned_to || prospect.created_by;
    const matchesPartner = partnerFilter === 'all' || prospectOwner === partnerFilter;
    return matchesSearch && matchesAcademy && matchesStatus && matchesPartner;
  });

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="leads-hot-title">
            Leads Hot
          </h1>
          <p className="text-slate-500 mt-1">Gerez vos etablissements a contacter</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-prospect-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Ajouter un Lead
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau Lead Hot</DialogTitle>
              <DialogDescription>
                Ajoutez un nouvel etablissement a votre liste
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de l&apos;etablissement *</Label>
                <Input
                  value={newProspect.name}
                  onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                  placeholder="College Jean Moulin"
                  data-testid="prospect-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Academie (optionnel)</Label>
                <Select
                  value={newProspect.academy}
                  onValueChange={(value) => setNewProspect({ ...newProspect, academy: value })}
                >
                  <SelectTrigger data-testid="prospect-academy-select">
                    <SelectValue placeholder="Selectionner une academie" />
                  </SelectTrigger>
                  <SelectContent>
                    {academies.map((academy) => (
                      <SelectItem key={academy} value={academy}>{academy}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Email de contact</Label>
                <Input
                  type="email"
                  value={newProspect.contact_email}
                  onChange={(e) => setNewProspect({ ...newProspect, contact_email: e.target.value })}
                  placeholder="contact@etablissement.fr"
                    data-testid="prospect-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    value={newProspect.contact_phone}
                    onChange={(e) => setNewProspect({ ...newProspect, contact_phone: e.target.value })}
                    placeholder="01 23 45 67 89"
                    data-testid="prospect-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={newProspect.notes}
                    onChange={(e) => setNewProspect({ ...newProspect, notes: e.target.value })}
                    placeholder="Informations complementaires..."
                    data-testid="prospect-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={createProspect} data-testid="submit-prospect-btn">
                  Creer le Lead
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-slate-100">
              <Building2 className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total prospects</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{prospects.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Filter className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Devis envoyes</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {prospects.filter(p => ['devis_envoye', 'contrat_envoye'].includes(p.status)).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <UserPlus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Factures envoyees</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {prospects.filter(p => p.status === 'facture_envoyee').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un établissement..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-prospects-input"
          />
        </div>
        <Select value={academyFilter} onValueChange={setAcademyFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="academy-filter">
            <MapPin className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Académie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les académies</SelectItem>
            {academies.map((academy) => (
              <SelectItem key={academy} value={academy}>{academy}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="partner-filter">
              <Users className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Partenaire" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les partenaires</SelectItem>
              {partners.map((partner) => (
                <SelectItem key={partner.user_id} value={partner.user_id}>
                  {partner.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Prospects table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="prospects-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Établissement</TableHead>
                <TableHead className="font-semibold">Académie</TableHead>
                {isAdmin && <TableHead className="font-semibold">Proprietaire</TableHead>}
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Statut</TableHead>
                <TableHead className="font-semibold">Signes</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProspects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-500">
                    Aucun prospect trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredProspects.map((prospect, index) => (
                  <TableRow 
                    key={prospect.prospect_id} 
                    className="table-row-hover animate-fade-in"
                    style={{ animationDelay: `${index * 0.02}s` }}
                    data-testid={`prospect-row-${prospect.prospect_id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{prospect.name}</p>
                        {prospect.notes && (
                          <p className="text-xs text-slate-500 truncate max-w-[200px]">{prospect.notes}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {prospect.academy}
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Select
                          value={prospect.assigned_to || prospect.created_by || ''}
                          onValueChange={(value) => reassignProspect(prospect.prospect_id, value)}
                        >
                          <SelectTrigger className="w-[150px] h-8 text-xs">
                            <SelectValue placeholder="Assigner">
                              {prospect.owner_name || 'Non assigne'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {partners.map((partner) => (
                              <SelectItem key={partner.user_id} value={partner.user_id}>
                                {partner.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="space-y-1">
                        {prospect.contact_email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{prospect.contact_email}</span>
                          </div>
                        )}
                        {prospect.contact_phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-3 h-3 text-slate-400" />
                            <span className="text-slate-600">{prospect.contact_phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[prospect.status]?.class} border`}>
                        {statusConfig[prospect.status]?.label || prospect.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {prospect.devis_signe && <CheckCircle className="w-4 h-4 text-emerald-500" title="Devis" />}
                        {prospect.accord_cadre_signe && <CheckCircle className="w-4 h-4 text-emerald-500" title="Accord" />}
                        {prospect.contrat_signe && <CheckCircle className="w-4 h-4 text-emerald-500" title="Contrat" />}
                        {!prospect.devis_signe && !prospect.accord_cadre_signe && !prospect.contrat_signe && (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/leads-hot/${prospect.prospect_id}`)}
                          title="Voir la fiche"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select
                          value={prospect.status}
                          onValueChange={(value) => updateProspectStatus(prospect.prospect_id, value)}
                        >
                          <SelectTrigger className="w-[140px] h-8" data-testid={`status-select-${prospect.prospect_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(statusConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key}>{config.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
